import {isFunction} from "lodash";
import PropTypes from "prop-types";
import React, {useCallback, useRef, useState} from "react";
import useOnClickOutside from "../../../../hooks/useOnClickOutside";
import {myID} from "../../../../libs/generateID";
import {isFiniteNumber} from "../../../../utils/MathUtils";
import Input from "../../input/Input";
import IconWithText from "../../label-info-icon-text/IconWithText";
import ReduxSlider from "../ReduxSlider";

const selfReturn = (v) => v;

// opens a focused input which calls onBlur when user click outside of it
const InputModal = ({onBlur, unit, hide}) => {
    const invokeCount = useRef(0);
    const input = useRef();

    // useRef will not work with the Input as it only accepts a function
    // so, we have to set it manually
    const setRef = useCallback(
        (ref) => {
            input.current = ref;
        },
        [input]
    );

    const stopPropagation = useCallback(
        (e) => {
            // it gets called on mount, and we don't want that
            if (invokeCount.current > 0) {
                hide();
            }
            invokeCount.current += 1;
            e.stopPropagation();
            e.preventDefault();
            return false;
        },
        [hide]
    );

    // prevent user from clicking an undesired element
    useOnClickOutside(input, stopPropagation, {eventType: "click"});

    const onKeyDown = useCallback(
        (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                e.target.blur();
                hide();
                return false;
            }
        },
        [hide]
    );

    return (
        <Input
            autofocus={true}
            inputRef={setRef}
            type={"text"}
            unit={unit}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        />
    );
};

const ReduxLabeledSlider = ({parseFromInput, format, unit, ...props}) => {
    const id = useRef(myID());
    const {
        label,
        input: {value, onChange},
        valueFormatter,
        required,
        infoIcon,
        infoText,
        disabled,
    } = props;
    const allowManualInput =
        (isFunction(parseFromInput) || !valueFormatter) && !disabled;

    const [show, setShow] = useState(false);
    const toggleShow = useCallback(() => {
        if (allowManualInput) {
            setShow((prevShow) => !prevShow);
        }
    }, [allowManualInput]);

    const tryParseValue = useCallback(
        (e) => {
            const text = e.target.value;
            try {
                if (text) {
                    const value = parseFromInput
                        ? parseFromInput(text)
                        : parseFloat(text);
                    console.log("tryParseValue %s to value %s", text, value);
                    if (isFiniteNumber(value)) {
                        onChange(value);
                    }
                }
            } catch (e) {
                console.warn(e);
            }
            // setTimeout(() => toggleShow(), 125);
        },
        [parseFromInput, onChange]
    );

    return (
        <React.Fragment>
            <div className="fetura-slider-label">
                <div>
                    <label>{label}</label>
                    <IconWithText icon={infoIcon} text={infoText} />
                    {required && <sup>*</sup>}
                </div>
                <div>
                    {!show && (
                        <strong
                            onClick={toggleShow}
                            className={allowManualInput ? "pointer" : null}
                            style={{float: "right"}}>
                            {allowManualInput && (
                                <i
                                    className={
                                        "fas fa-pen fa-fw opacity-25 me-1"
                                    }
                                />
                            )}
                            {valueFormatter
                                ? valueFormatter(value)
                                : selfReturn(value)}
                        </strong>
                    )}
                    {show && (
                        <InputModal
                            hide={toggleShow}
                            type={"text"}
                            unit={unit}
                            onBlur={tryParseValue}
                        />
                    )}
                </div>
            </div>
            <ReduxSlider
                {...props}
                id={props.id ?? id.current}
                min={props.min ?? 0}
                max={props.max ?? 100}
                step={props.step ?? 1}
                disabled={props.disabled ?? false}
                showButtons={props.showButtons ?? false}
                onChange={props.onChange ?? selfReturn}
                valueFormatter={valueFormatter}
            />
        </React.Fragment>
    );
};
export default ReduxLabeledSlider;

ReduxLabeledSlider.propTypes = {
    id: PropTypes.string,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    infoText: PropTypes.string,
    infoIcon: PropTypes.oneOf(["info", "warning", "error"]),
    step: PropTypes.number,
    isRange: PropTypes.bool,
    disabled: PropTypes.bool,
    valueFormatter: PropTypes.func,
    tipFormatter: PropTypes.func,
    showButtons: PropTypes.bool,
    parse: PropTypes.func,
    unit: PropTypes.string,
};
